import { EllipsisOutlined, LikeOutlined } from '@ant-design/icons'
import { getOperationName } from '@apollo/client/utilities'
import { Button, Comment, Dropdown, Menu } from 'antd'
import { Popover } from 'antd'
import cls from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { TextContent, Timestamp, UserHoverCard } from '@/components'
import { UserAvatar } from '@/components'
import {
  GetCommentLikedUsersDocument,
  RequestedCommentFragment,
  RequestedPostFragment,
  useCommentLikeToggleMutation,
  useGetCommentLikedUsersQuery,
} from '@/generated/graphql'
import { useIsSystemAdmin } from '@/modules/admin/admin.hooks'
import { routes } from '@/routes'

import { PostCommentEdit } from './post-comment-edit.component'
import { useDeleteComment, useUpdateComment } from './post-comment.hooks'
import s from './post-comments.module.less'

interface PostCommentItem {
  post: RequestedPostFragment
  comment: RequestedCommentFragment
  onClickReply: (mention: string) => void
  disableCreate?: boolean
}

export const PostCommentItem: React.FC<PostCommentItem> = ({
  post,
  comment,
  onClickReply,
  disableCreate = false,
  children,
}) => {
  const [t] = useTranslation()
  const isSystemAdmin = useIsSystemAdmin()
  const [isVisible, setIsVisible] = React.useState(false)

  const [updateComment] = useUpdateComment()
  const onDelete = useDeleteComment(comment)
  const [changeLike, { loading }] = useCommentLikeToggleMutation()
  const [isEdit, setEdit] = React.useState(false)

  const { data: likedUsers } = useGetCommentLikedUsersQuery({
    skip: !isVisible,
    variables: {
      postId: post.postId,
      commentId: comment.commentId,
    },
  })

  const onChangeComment = React.useCallback(
    (content: string) => {
      if (comment.isViewerCreated) {
        updateComment({
          variables: {
            comment: {
              commentId: comment.commentId,
              content,
            },
          },
        })
      }
    },
    [comment]
  )

  const onReply = () => {
    const s = comment.isViewerCreated || !comment.creator ? '' : `@${comment.creator.email.split('@')[0]} ` // 末尾需要一个空格隔开
    onClickReply(s)
  }

  const replyButton = !disableCreate && (
    <div className={s.actionButton} onClick={onReply}>
      {t('post.reply')}
    </div>
  )

  const likedUsersList = (
    <div className={s.likedUsers}>
      {likedUsers?.post.comment.likedUsers.edges
        .map(({ node }) => node)
        .reverse()
        .map((u) => (
          <div key={u.userId} className={s.likedUser}>
            <UserHoverCard key={u.userId} email={u.email}>
              <UserAvatar size='default' user={u} />
            </UserHoverCard>
          </div>
        ))}
    </div>
  )

  const likeClick = () => {
    if (loading) {
      return
    }

    changeLike({
      variables: {
        commentId: comment.commentId,
        like: !comment.liked,
      },
      refetchQueries: [getOperationName(GetCommentLikedUsersDocument)!],
    })
  }

  const likedIcon = <LikeOutlined />
  const likedText = comment.likedCount > 0 && ` (${comment.likedCount})`

  const liked = (
    <div onClick={likeClick}>
      {likedIcon}
      {comment.likedCount > 0 && likedText}
    </div>
  )

  const likeButton = (
    <div className={cls(s.actionButton, { [s.active]: comment.liked })}>
      {comment.likedCount > 0 ? (
        <Popover content={likedUsersList} onVisibleChange={(visible) => setIsVisible(visible)}>
          {liked}
        </Popover>
      ) : (
        liked
      )}
    </div>
  )

  const dropdownMenu = (
    <Menu style={{ width: 80 }}>
      <Menu.Item onClick={() => setEdit(true)}>{t('post.edit')}</Menu.Item>
      <Menu.Item onClick={() => onDelete()}>{t('post.delete')}</Menu.Item>
    </Menu>
  )

  const commentDropdown = (comment.isViewerCreated || isSystemAdmin) && (
    <Dropdown trigger={['click']} overlay={dropdownMenu} className={s.commentMenu}>
      <Button size='small' shape='circle' icon={<EllipsisOutlined />} />
    </Dropdown>
  )

  const content = (
    <div className={s.commentContentContainer}>
      <TextContent content={comment.content} mentions={comment.mentions} isMarkdown={true} onChange={onChangeComment} />
      {commentDropdown}
    </div>
  )

  const editContent = <PostCommentEdit comment={comment} onClose={() => setEdit(false)} />

  const author = !comment.creator ? (
    'Agoran'
  ) : (
    <UserHoverCard email={comment.creator.email}>
      <Link to={routes.users.page(comment.creator.email)}>{comment.creator.name}</Link>
    </UserHoverCard>
  )

  return (
    <Comment
      className={s.commentItem}
      actions={
        isEdit ? [] : [replyButton, disableCreate ? undefined : <div key='dot'>·</div>, likeButton].filter(Boolean)
      }
      author={author}
      avatar={<UserAvatar user={comment.creator} />}
      datetime={<Timestamp time={comment.createdAt} />}
      content={isEdit ? editContent : content}
    >
      {children}
    </Comment>
  )
}
